import type { GpuCostsDto } from '@studio/shared';
import { useQuery } from '@tanstack/react-query';
import { GpuCard } from '@/components/GpuCard';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { dateTime, duration, money } from '@/lib/utils';

export function GpuPage() {
  const costs = useQuery({
    queryKey: ['gpu-costs'],
    queryFn: () => api<GpuCostsDto>('/api/gpu/costs'),
    refetchInterval: 15_000,
  });
  const c = costs.data;
  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title="GPU & costs"
        description="The GPU is rented on RunPod (Iceland/Norway only) when shots are waiting, and given back when nothing is left to make."
      />
      <GpuCard />
      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">GPU cost (all rentals)</div>
          <div className="text-2xl font-semibold tabular">{money(c?.totalCost)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">GPU time</div>
          <div className="text-2xl font-semibold tabular">{c ? `${c.totalHours.toFixed(2)} h` : '—'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">Shots made</div>
          <div className="text-2xl font-semibold tabular">{c?.totalShots ?? '—'}</div>
          {c && c.totalShots > 0 && (
            <div className="text-xs text-muted-foreground">{money(c.totalCost / c.totalShots, 3)} per shot</div>
          )}
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Every GPU rental</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Started</TableHead>
                <TableHead>GPU</TableHead>
                <TableHead>Time on</TableHead>
                <TableHead>$/h</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Shots</TableHead>
                <TableHead>Stopped because</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {c?.runs.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap">
                    {dateTime(r.startedAt)} {r.mode === 'fake' && <Badge variant="warning">fake</Badge>}
                  </TableCell>
                  <TableCell>
                    <div>{r.gpuType}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.dataCenter} · pod {r.podId}
                    </div>
                  </TableCell>
                  <TableCell className="tabular">
                    {duration((r.stoppedAt ? Date.parse(r.stoppedAt) : Date.now()) - Date.parse(r.startedAt))}
                  </TableCell>
                  <TableCell className="tabular">{money(r.costPerHr)}</TableCell>
                  <TableCell className="tabular">{money(r.cost, 3)}</TableCell>
                  <TableCell className="tabular">{r.shots}</TableCell>
                  <TableCell className="text-xs">
                    {r.stoppedAt ? r.stopReason : <Badge variant="success">running</Badge>}
                  </TableCell>
                </TableRow>
              ))}
              {c && !c.runs.length && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No GPU rented yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
